import { TranscriptionMessage } from 'src/models';
import moment from 'moment';

export const downloadTranscription = (
  messages: TranscriptionMessage[],
  config: { roles: Record<string, { label: string }>; filename?: string }
) => {
  const { roles, filename } = config;

  const blob = new Blob(
    [
      messages
        .filter((msg) => msg.role && roles[msg.role])
        .map((msg) => `${roles[msg.role!].label}:\n${msg.content ?? ''}\n`)
        .join('\n'),
    ],
    { type: 'text/plain' }
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'transcription.txt';
  a.click();
};

export const sortTranscripts = (transcripts: TranscriptionMessage[]) => {
  return transcripts.sort(
    (a: any, b: any) =>
      moment(b.timestamp).valueOf() - moment(a.timestamp).valueOf()
  );
};

export const combineTranscripts = (params: {
  existingTranscripts: TranscriptionMessage[];
  liveTranscript: TranscriptionMessage[];
  filterDuplicates?: boolean;
  sort?: boolean;
}) => {
  const {
    existingTranscripts,
    liveTranscript,
    filterDuplicates = true,
    sort = true,
  } = params;

  let combinedTranscripts;

  if (filterDuplicates) {
    // When actively transcribing, filter out potential duplicates
    const newLiveTranscripts = liveTranscript.filter((liveItem) => {
      const timestampMinute = moment(liveItem.timestamp).format(
        'YYYY-MM-DDTHH:mm:ss'
      );

      // Check if this item already exists in fullTranscript
      return !existingTranscripts.some(
        (fullItem) =>
          moment(fullItem.timestamp).format('YYYY-MM-DDTHH:mm:ss') ===
            timestampMinute &&
          fullItem.speaker === liveItem.speaker &&
          liveItem.text &&
          fullItem.text?.includes(liveItem.text || '')
      );
    });

    // Only proceed if there are new unique items
    if (newLiveTranscripts.length === 0) return existingTranscripts;

    combinedTranscripts = [...existingTranscripts, ...newLiveTranscripts];
  } else {
    // When not transcribing (processing final results), use original approach
    if (!liveTranscript.length) return existingTranscripts;
    combinedTranscripts = [...existingTranscripts, ...liveTranscript];
  }

  const groupedTranscripts = combinedTranscripts.reduce((acc, current) => {
    // Format timestamp to the minute in browser timezone
    const timestampMinute = moment(current.timestamp).format(
      'YYYY-MM-DDTHH:mm:ss'
    );

    // Find if there's already an entry for this timestamp and speaker
    const existingEntryIndex = acc.findIndex(
      (entry: any) =>
        entry.timestamp === timestampMinute && entry.speaker === current.speaker
    );

    if (existingEntryIndex !== -1) {
      // Only append if the text isn't already included when transcribing
      // Otherwise just append as in the original code
      if (
        filterDuplicates &&
        acc[existingEntryIndex].text?.includes(current.text || '')
      ) {
        // Skip if already included during active transcription
      } else {
        acc[existingEntryIndex].text += ` ${current.text}`;
      }
    } else {
      // If it's a new minute or a new speaker, push a new entry
      // @ts-expect-error
      acc.push({
        timestamp: timestampMinute,
        text: current.text,
        speaker: current.speaker,
      });
    }
    return acc;
  }, [] as TranscriptionMessage[]);

  if (sort) {
    return sortTranscripts(groupedTranscripts);
  }

  return groupedTranscripts;
};
